import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { useNavigate } from 'react-router-dom';
import { HiShieldCheck } from 'react-icons/hi';
import { useSelector, useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import { updateProfile, loginSuccess } from "../features/auth/authSlice";
import axios from "axios";
import { FaUser } from "react-icons/fa";
import { api } from "../utils/apiUtils";
import { BASE_URL } from "../config/apiConfig";

const Profile = () => {
  const { user, isLoading, isError, message } = useSelector(
    (state) => state.auth,
  );
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const corporateToken = localStorage.getItem("corporateToken");
  const corporateMember = (() => {
    try {
      const raw = localStorage.getItem("corporateMember");
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  })();

  const isCorporateSession = !!corporateToken && !user;

  const [corporateMe, setCorporateMe] = useState(null);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    state: "",
    pincode: "",
    nationality: "India",
    dob: "",
    marital_status: "Single",
    gender: "Male",
  });

  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [activeTab, setActiveTab] = useState("profile");
  const [isSaving, setIsSaving] = useState(false);
  const [profileLoaded, setProfileLoaded] = useState(false);

  // API URL
  // Import API URL from config
  const { API_URL } = require("../config/apiConfig");

  // Fetch latest user profile from API
  const fetchUserProfile = async () => {
    try {
      // Check for token from any authentication source (corporate, user, vendor, driver, admin)
      const token =
        localStorage.getItem("corporateToken") ||
        localStorage.getItem("token") ||
        localStorage.getItem("vendorToken") ||
        localStorage.getItem("driverToken") ||
        localStorage.getItem("adminToken");
      if (!token) {
        console.log("No token found");
        return;
      }

      console.log("Fetching profile from:", `${API_URL}/auth/me`);
      const response = await axios.get(
        `${API_URL}/auth/me`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      console.log("Profile response:", response.data);

      if (response.data.success && response.data.data) {
        const userData = response.data.data;
        console.log("User data phone:", userData.phone);
        setFormData({
          name: userData.name || "",
          email: userData.email || "",
          phone: userData.phone || "",
          address: userData.address || "",
          city: userData.city || "",
          state: userData.state || "",
          pincode: userData.pincode || "",
          nationality: userData.nationality || "India",
          dob: userData.dob ? userData.dob.split('T')[0] : "",
          marital_status: userData.marital_status || "Single",
          gender: userData.gender || "Male",
        });

        if (userData.image && userData.image.startsWith('http')) {
          // Only set preview for valid Cloudinary URLs (must start with https://)
          setImagePreview(userData.image);
        } else {
          // Clear old local storage paths and use fallback avatar
          setImagePreview(null);
        }
        setProfileLoaded(true);
      } else {
        console.error("API response missing success or data:", response.data);
        setProfileLoaded(true);
      }
    } catch (error) {
      console.error("Error fetching user profile:", error);
      console.error("Error details:", error.response?.data);
      setProfileLoaded(true);
    }
  };

  // Load user data and fetch bookings when component mounts
  useEffect(() => {
    // Check for token from any authentication source (corporate, user, vendor, driver, admin)
    const token =
      localStorage.getItem("corporateToken") ||
      localStorage.getItem("token") ||
      localStorage.getItem("vendorToken") ||
      localStorage.getItem("driverToken") ||
      localStorage.getItem("adminToken");

    // Always try to fetch profile if token exists (this is more reliable)
    if (token) {
      console.log("Token found, fetching profile");
      fetchUserProfile();
    } else if (corporateToken && corporateMember) {
      console.log("Setting up corporate member data");
      setFormData({
        name: corporateMember.fullName || "Corporate Staff",
        email: corporateMember.email || "",
        phone: corporateMember.mobileNumber || "",
        address: "",
        city: "",
        state: "",
        pincode: "",
        nationality: "India",
        dob: "",
        marital_status: "Single",
        gender: "Male",
      });
      setIsEditing(false);
    } else {
      console.log("No token or corporate session found");
    }
  }, []);

  useEffect(() => {
    if (!isCorporateSession) return;

    (async () => {
      try {
        const res = await api.get("/auth/me");
        if (res?.data?.success) {
          setCorporateMe(res.data.data);
        }
      } catch (e) {
      }
    })();
  }, [isCorporateSession]);

  // Show toast messages on error
  useEffect(() => {
    if (isError) {
      toast.error(message);
    }
  }, [isError, message]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file size (20MB max for profile images)
      const maxSizeInBytes = 20 * 1024 * 1024; // 20MB
      if (file.size > maxSizeInBytes) {
        toast.error(`Image size must be less than 20MB. Your file is ${(file.size / 1024 / 1024).toFixed(2)}MB`);
        return;
      }

      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast.error("Please select a valid image file");
        return;
      }

      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isCorporateSession) {
      toast.info("Corporate staff profile is managed by your Corporate Admin");
      setIsEditing(false);
      return;
    }

    // Validate fields
    if (!formData.name || !formData.email) {
      toast.error("Name and email are required");
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error("Please enter a valid email address");
      return;
    }

    setIsSaving(true);
    try {
      const token = localStorage.getItem("token");

      if (!token) {
        toast.error("Not authenticated. Please log in again.");
        setIsSaving(false);
        return;
      }

      const formDataToSend = new FormData();

      // Add all fields (except phone - cannot be changed)
      formDataToSend.append("name", formData.name);
      formDataToSend.append("email", formData.email);
      formDataToSend.append("address", formData.address);
      formDataToSend.append("city", formData.city);
      formDataToSend.append("state", formData.state);
      formDataToSend.append("pincode", formData.pincode);
      formDataToSend.append("nationality", formData.nationality);
      formDataToSend.append("dob", formData.dob);
      formDataToSend.append("marital_status", formData.marital_status);
      formDataToSend.append("gender", formData.gender);

      // Add image if selected
      if (imageFile) {
        formDataToSend.append("image", imageFile);
      }

      console.log("Sending profile update to:", `${API_URL}/auth/edit-profile`);

      // Show loading toast for image uploads (large files take time)
      if (imageFile) {
        toast.loading("Uploading image... This may take a minute for large files");
      }

      const response = await axios.put(
        `${API_URL}/auth/edit-profile`,
        formDataToSend,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            // Let axios/browser handle Content-Type for FormData
          },
          // Increase timeout for large file uploads (2 minutes 30 seconds)
          timeout: 150000,
        }
      );

      if (response.data.success) {
        toast.dismiss(); // Clear loading toast
        toast.success("Profile updated successfully!");

        // Update formData with the response data
        const updatedData = response.data.data;
        setFormData({
          name: updatedData.name || "",
          email: updatedData.email || "",
          phone: updatedData.phone || "",
          address: updatedData.address || "",
          city: updatedData.city || "",
          state: updatedData.state || "",
          pincode: updatedData.pincode || "",
          nationality: updatedData.nationality || "India",
          dob: updatedData.dob ? updatedData.dob.split('T')[0] : "",
          marital_status: updatedData.marital_status || "Single",
          gender: updatedData.gender || "Male",
        });

        // Update image preview if image was uploaded (only Cloudinary URLs)
        if (updatedData.image && updatedData.image.startsWith('http')) {
          setImagePreview(updatedData.image);
        }

        // Update Redux state with the updated user data
        const updatedUserState = {
          ...user,
          user: updatedData
        };
        dispatch(loginSuccess(updatedUserState));
        localStorage.setItem("user", JSON.stringify(updatedUserState));

        setIsEditing(false);
        setImageFile(null);

        // Optionally fetch latest data from server to ensure sync
        try {
          await fetchUserProfile();
        } catch (fetchError) {
          console.warn("Could not fetch updated profile, but update succeeded:", fetchError);
        }
      } else {
        toast.dismiss(); // Clear loading toast
        const errorMsg = response.data?.message || "Failed to update profile. Please try again.";
        toast.error(errorMsg);
        console.error("Update failed:", response.data);
      }
    } catch (error) {
      toast.dismiss(); // Clear loading toast
      console.error("Profile update error:", error);

      // Get detailed error message
      let errorMessage = "Failed to update profile. Please try again.";

      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.code === 'ECONNABORTED') {
        errorMessage = "Upload took too long. Please try with a smaller image or check your internet connection.";
      } else if (error.message) {
        errorMessage = error.message;
      }

      console.error("Detailed error:", {
        message: errorMessage,
        status: error.response?.status,
        data: error.response?.data,
      });

      toast.error(errorMessage);
    } finally {
      setIsSaving(false);
    }
  };



  return (
    <div className="max-w-[1180px] mx-auto py-8 px-4 mt-16">
      {/* Profile Header */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="flex flex-col md:flex-row items-center md:items-start justify-between">
          <div className="flex flex-col md:flex-row items-center">
            <div className="relative w-24 h-24 rounded-full bg-blue-600 text-white flex items-center justify-center text-4xl font-bold mb-4 md:mb-0 overflow-hidden">
              {imagePreview ? (
                <img
                  src={imagePreview}
                  alt="Profile"
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    console.warn("Profile image not available, using fallback avatar");
                    setImagePreview(null);
                  }}
                />
              ) : (
                <span className="text-white font-bold text-3xl">
                  {formData.name ? formData.name.charAt(0).toUpperCase() : "U"}
                </span>
              )}
            </div>
            <div className="md:ml-6 text-center md:text-left">
              <h1 className="text-2xl font-bold">{formData.name}</h1>
              {isCorporateSession && (
                <p className="text-gray-700 mb-1">
                  {((corporateMe?.role || corporateMember?.role || "staff") === "admin")
                    ? "Corporate Admin"
                    : "Corporate Staff"}
                </p>
              )}
              {isCorporateSession && (
                <p className="text-gray-600 mb-2">{corporateMe?.companyName || corporateMe?.company || ""}</p>
              )}
              <p className="text-gray-600 mb-2">{formData.email}</p>
              <p className="text-gray-700 font-medium">
                {formData.phone ? `Phone: ${formData.phone}` : "Phone: Fetching..."}
              </p>
            </div>
          </div>

          {!isCorporateSession && (
            <div className="mt-4 md:mt-0 flex items-center space-x-3">
              <button
                onClick={() =>
                  setActiveTab("profile") || setIsEditing(!isEditing)
                }
                className={`px-4 py-2 rounded ${isEditing && activeTab === "profile" ? "bg-gray-200 text-gray-800" : "bg-blue-600 text-white"}`}
              >
                {isEditing && activeTab === "profile"
                  ? "Cancel Editing"
                  : "Edit Profile"}
              </button>

              <button
                onClick={() => {
                  const role = (user && user.user && user.user.role) ? user.user.role : 'user';
                  // If user is logged in, navigate to change-password (requires current password)
                  if (user) {
                    navigate(`/${role}/change-password`);
                  } else {
                    // Otherwise, go to forgot-password flow (send OTP)
                    navigate(`/${role}/forgot-password/send-otp`);
                  }
                }}
                className="px-4 py-2 rounded bg-amber-500 text-white flex items-center space-x-2 hover:brightness-95"
              >
                <HiShieldCheck className="h-4 w-4" />
                <span>Change Password</span>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="flex border-b mb-6 overflow-x-auto">
        <button
          onClick={() => setActiveTab("profile")}
          className={`px-4 py-2 font-medium ${activeTab === "profile" ? "border-b-2 border-blue-600 text-blue-600" : "text-gray-500"} flex items-center`}
        >
          <FaUser className="mr-2" /> Profile
        </button>
      </div>

      {/* Tab Content */}
      <div className="bg-white rounded-lg shadow-md p-6">
        {/* Profile Tab */}
        {activeTab === "profile" && (
          <>
            {isEditing ? (
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Profile Picture Upload */}
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
                  <label className="block text-gray-700 font-semibold mb-2">Profile Picture</label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    className="w-full"
                  />
                  {imagePreview && (
                    <div className="mt-4">
                      <img src={imagePreview} alt="Preview" className="w-24 h-24 rounded-full mx-auto object-cover" />
                    </div>
                  )}
                </div>

                {/* Basic Information */}
                <div>
                  <h3 className="text-lg font-semibold mb-3 text-gray-800">Basic Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-gray-700 mb-1">Full Name</label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        className="w-full border rounded p-2"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-gray-700 mb-1">Email</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        className="w-full border rounded p-2"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-gray-700 mb-1">Phone <span className="text-gray-500 text-xs">(Cannot be changed)</span></label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        disabled
                        className="w-full border rounded p-2 bg-gray-50 cursor-not-allowed text-gray-900"
                      />
                    </div>
                    <div>
                      <label className="block text-gray-700 mb-1">Date of Birth</label>
                      <input
                        type="date"
                        name="dob"
                        value={formData.dob}
                        onChange={handleChange}
                        className="w-full border rounded p-2"
                      />
                    </div>
                    <div>
                      <label className="block text-gray-700 mb-1">Gender</label>
                      <select
                        name="gender"
                        value={formData.gender}
                        onChange={handleChange}
                        className="w-full border rounded p-2"
                      >
                        <option value="Male">Male</option>
                        <option value="Female">Female</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-gray-700 mb-1">Marital Status</label>
                      <select
                        name="marital_status"
                        value={formData.marital_status}
                        onChange={handleChange}
                        className="w-full border rounded p-2"
                      >
                        <option value="Single">Single</option>
                        <option value="Married">Married</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Address Information */}
                <div>
                  <h3 className="text-lg font-semibold mb-3 text-gray-800">Address Information</h3>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-gray-700 mb-1">Address</label>
                      <textarea
                        name="address"
                        value={formData.address}
                        onChange={handleChange}
                        className="w-full border rounded p-2"
                        rows="3"
                      ></textarea>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-gray-700 mb-1">City</label>
                        <input
                          type="text"
                          name="city"
                          value={formData.city}
                          onChange={handleChange}
                          className="w-full border rounded p-2"
                        />
                      </div>
                      <div>
                        <label className="block text-gray-700 mb-1">State</label>
                        <input
                          type="text"
                          name="state"
                          value={formData.state}
                          onChange={handleChange}
                          className="w-full border rounded p-2"
                        />
                      </div>
                      <div>
                        <label className="block text-gray-700 mb-1">Pincode</label>
                        <input
                          type="text"
                          name="pincode"
                          value={formData.pincode}
                          onChange={handleChange}
                          className="w-full border rounded p-2"
                        />
                      </div>
                      <div>
                        <label className="block text-gray-700 mb-1">Nationality</label>
                        <input
                          type="text"
                          name="nationality"
                          value={formData.nationality}
                          onChange={handleChange}
                          className="w-full border rounded p-2"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => setIsEditing(false)}
                    className="bg-gray-400 text-white px-4 py-2 rounded hover:bg-gray-500"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSaving}
                    className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 disabled:bg-green-400"
                  >
                    {isSaving ? "Saving..." : "Save Changes"}
                  </button>
                </div>
              </form>
            ) : (
              <div className="space-y-6">
                {/* Personal Information */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h3 className="text-lg font-semibold mb-4">Personal Information</h3>
                    <div className="space-y-3">
                      <div>
                        <p className="text-gray-600 text-sm">Full Name</p>
                        <p className="font-medium">{formData.name}</p>
                      </div>
                      <div>
                        <p className="text-gray-600 text-sm">Email</p>
                        <p className="font-medium">{formData.email}</p>
                      </div>
                      <div>
                        <p className="text-gray-600 text-sm">Phone</p>
                        <p className="font-medium">{formData.phone || "Not provided"}</p>
                      </div>
                      <div>
                        <p className="text-gray-600 text-sm">Gender</p>
                        <p className="font-medium">{formData.gender || "Not provided"}</p>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold mb-4">Additional Information</h3>
                    <div className="space-y-3">
                      <div>
                        <p className="text-gray-600 text-sm">Date of Birth</p>
                        <p className="font-medium">{formData.dob ? new Date(formData.dob).toLocaleDateString() : "Not provided"}</p>
                      </div>
                      <div>
                        <p className="text-gray-600 text-sm">Marital Status</p>
                        <p className="font-medium">{formData.marital_status || "Not provided"}</p>
                      </div>
                      <div>
                        <p className="text-gray-600 text-sm">Nationality</p>
                        <p className="font-medium">{formData.nationality || "Not provided"}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Address Information */}
                <div>
                  <h3 className="text-lg font-semibold mb-4">Address Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <p className="text-gray-600 text-sm">Address</p>
                      <p className="font-medium">{formData.address || "Not provided"}</p>
                    </div>
                    <div>
                      <p className="text-gray-600 text-sm">City</p>
                      <p className="font-medium">{formData.city || "Not provided"}</p>
                    </div>
                    <div>
                      <p className="text-gray-600 text-sm">State</p>
                      <p className="font-medium">{formData.state || "Not provided"}</p>
                    </div>
                    <div>
                      <p className="text-gray-600 text-sm">Pincode</p>
                      <p className="font-medium">{formData.pincode || "Not provided"}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        )}


      </div>
    </div>
  );
};

export default Profile;
